import React, { useMemo, useContext } from 'react';
import { Number } from '../../common/common';
import { useTable, useSortBy, useExpanded } from 'react-table';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCaretDown,
  faSort,
  faCaretUp,
  faPlus,
  faMinus,
} from '@fortawesome/free-solid-svg-icons';
import { ReactComponent as CirclePlus } from '../../../../assets/icons/circle-plus.svg';
import { ReactComponent as CircleMinus } from '../../../../assets/icons/circle-minus.svg';
import AppContext from '../../../context';

const ActivePosTableMob = (props) => {
  const context = useContext(AppContext);

  const get_data_class = (data) => {
    const color_class = data < 0 ? 'negative' : 'positive';
    return color_class;
  };

  const columns = useMemo(() => {
    if (props.summary) {
      const config = [
        {
          id: 'expander',
          Header: `${
            props.mode === 'by_portfolio' ? 'Portfolio' : 'Instrument'
          }`,
          accessor: 'name',
          Cell: (props) => {
            return (
              <div className={`${!props.row.canExpand ? 'ml-2' : ''}`}>
                <span
                  className={`${
                    !props.row.canExpand ? 'font-weight-normal' : ''
                  }`}
                >
                  {props.value}
                </span>
                {props.row.canExpand && (
                  <span
                    {...props.row.getToggleRowExpandedProps()}
                    className="toggler"
                  >
                    {props.row.isExpanded ? <CircleMinus /> : <CirclePlus />}
                  </span>
                )}
                <div className="weight">
                  <span className="text">Weight</span>
                  {props.row.original.weight}%
                </div>
              </div>
            );
          },
          thClass: 'th-name',
          tdClass: 'table-data name',
          disableSortBy: true,
        },
        {
          Header: '1D Chg',
          accessor: 'one_day_change',
          Cell: (props) => {
            return (
              <>
                <div className="align-middle">
                  <Number
                    value={props.row.original.one_day_change}
                    format={true}
                  />
                </div>{' '}
                <span className="pill">
                  {props.row.original.one_day_change_perc}%
                </span>
              </>
            );
          },
          sortType: (rowA, rowB, id, desc) => {
            if (!rowA.depth) return false;
            let a = rowA.original['one_day_change_perc'];
            let b = rowB.original['one_day_change_perc'];
            if (a > b) return 1;
            if (a < b) return -1;
            return 0;
          },
          thClass: 'pl-5',
          tdClass: 'table-data',
        },
        {
          Header: 'P&L',
          accessor: 'gain_loss',
          Cell: (props) => {
            return (
              <>
                <div className="align-middle">
                  <Number value={props.row.original.gain_loss} format={true} />
                </div>{' '}
                <span className="pill">
                  {props.returnsType === 'xirr'
                    ? props.row.original.gain_loss_perc
                    : props.row.original.gain_loss_abs_perc}
                  %
                </span>
              </>
            );
          },
          sortType: (rowA, rowB, id, desc) => {
            if (!rowA.depth) return false;
            let datum =
              props.returnsType === 'xirr'
                ? 'gain_loss_perc'
                : 'gain_loss_abs_perc';
            let a = rowA.original[datum] === '-' ? 0 : rowA.original[datum];
            let b = rowB.original[datum] === '-' ? 0 : rowB.original[datum];
            if (a > b) return 1;
            if (a < b) return -1;
            return 0;
          },
          thClass: 'pl-5',
          tdClass: 'table-data',
        },
        {
          Header: 'Current Amt',
          accessor: 'unrealised',
          Cell: (props) => {
            return (
              <>
                <div>
                  <Number value={props.value} format={true} />
                </div>
                <div className="invested">
                  <span className="text" title="Invested">
                    Inv
                  </span>
                  <Number value={props.row.original.cost} format={true} />
                </div>
              </>
            );
          },
          thClass: 'pl-4 icon-left',
          tdClass: 'table-data',
          sortType: (rowA, rowB, id, desc) => {
            if (!rowA.depth) return false;
            let a = rowA.values[id];
            let b = rowB.values[id];
            if (a > b) return 1;
            if (a < b) return -1;
            return 0;
          },
        },
      ];
      return config;
    }
    return [];
  }, [props.summary, props.returnsType]);

  const data = useMemo(() => {
    if (props.summary) {
      return props.summary;
    }
    return [];
  }, [props.summary]);

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
    toggleAllRowsExpanded,
  } = useTable(
    {
      columns,
      data,
      getSubRows: (row) => row.sub_rows,
    },
    useSortBy,
    useExpanded,
  );

  const generateSortingIndicator = (column) => {
    return column.isSorted ? (
      column.isSortedDesc ? (
        <FontAwesomeIcon icon={faCaretDown} className="sort-icon" />
      ) : (
        <FontAwesomeIcon icon={faCaretUp} className="sort-icon" />
      )
    ) : (
      <FontAwesomeIcon icon={faSort} className="sort-icon" />
    );
  };

  return (
    <table id="active-pos" className="mob family-pf data-table mt-0 mb-1">
      <thead>
        {headerGroups.map((headerGroup) => (
          <tr {...headerGroup.getHeaderGroupProps()}>
            {headerGroup.headers.map((column) => (
              <th
                {...column.getHeaderProps([
                  {
                    className: column.thClass,
                  },
                  column.getSortByToggleProps(),
                ])}
                onClick={(e) => {
                  if (!column.disableSortBy) {
                    column.getSortByToggleProps().onClick(e);
                    if (rows.some((r) => r.isExpanded)) return;
                    toggleAllRowsExpanded(true);
                  }
                }}
              >
                {column.render('Header')}
                {!column.disableSortBy && generateSortingIndicator(column)}
              </th>
            ))}
          </tr>
        ))}
      </thead>
      <tbody {...getTableBodyProps()}>
        {rows.map((row) => {
          prepareRow(row);
          return (
            <tr {...row.getRowProps()} className="table-row">
              {row.cells.map((cell) => {
                return (
                  <td
                    {...cell.getCellProps({
                      className: cell.column.tdClass,
                    })}
                    className={`${cell.column.tdClass} ${
                      ['one_day_change', 'gain_loss'].includes(cell.column.id)
                        ? get_data_class(cell.value)
                        : ''
                    }`}
                  >
                    {cell.render('Cell', { ...props })}
                  </td>
                );
              })}
            </tr>
          );
        })}
        {props.total && (
          <tr className="total-row">
            <td>TOTAL</td>
            <td
              className={
                get_data_class(props.total.one_day_change) + ' text-right'
              }
            >
              <div className="align-middle">
                <Number value={props.total.one_day_change} format={true} />
              </div>
              <span className="pill">{props.total.one_day_change_perc}%</span>
            </td>
            <td
              className={get_data_class(props.total.gain_loss) + ` text-right`}
            >
              <div className="align-middle">
                <Number value={props.total.gain_loss} format={true} />{' '}
              </div>
              <span className="pill">
                {props.returnsType === 'xirr'
                  ? props.total.gain_loss_perc
                  : props.total.gain_loss_abs_perc}
                %
              </span>
            </td>
            <td className="text-right">
              <div>
                <Number value={props.total.unrealised} format={true} />
              </div>
              <div className="invested">
                <span className="text" title="Invested">
                  Inv
                </span>
                <Number value={props.total.cost} format={true} />
              </div>
            </td>
          </tr>
        )}
      </tbody>
    </table>
  );
};

export default ActivePosTableMob;
